// 辅助函数：判断是否是标题类型
const isHeadingType = (type) => {
    return type === 'heading1' || type === 'heading2' || type === 'heading3';
};

// 辅助函数：获取标题等级
const getHeadingLevel = (type) => {
    if (type === 'heading1') return 1;
    if (type === 'heading2') return 2;
    if (type === 'heading3') return 3;
    return 0;
};

// 将API数据转换为Tiptap编辑器内容格式
export const convertBlocksToTiptapContent = (blocks) => {
    // 第一步：收集所有 heading 信息
    const headingInfos = [];
    blocks.forEach((block, index) => {
        if (isHeadingType(block.type)) {
            const level = getHeadingLevel(block.type);
            headingInfos.push({
                index,
                level,
                blockId: block.id,
            });
        }
    });

    // 第二步：判断每个 heading 是否是最低一节
    const isLowestChapterMap = new Map();
    headingInfos.forEach((headingInfo, headingIndex) => {
        const { level, index: blockIndex } = headingInfo;
        let isLowestChapter = false;

        if (level === 3) {
            // h3 总是最低一节
            isLowestChapter = true;
        } else {
            // 找到下一个同级或更高级的 heading
            let nextHeadingIndex = headingIndex + 1;
            while (nextHeadingIndex < headingInfos.length) {
                const nextHeading = headingInfos[nextHeadingIndex];
                if (nextHeading.level <= level) {
                    // 找到了下一个同级或更高级的 heading
                    break;
                }
                nextHeadingIndex++;
            }

            // 检查从当前 heading 到下一个同级或更高级 heading 之间，是否有更低级的 heading
            let hasLowerLevelHeading = false;
            if (level === 1) {
                // h1: 检查是否有 h2 或 h3
                for (let i = blockIndex + 1; i < blocks.length; i++) {
                    if (
                        i >=
                        (nextHeadingIndex < headingInfos.length
                            ? headingInfos[nextHeadingIndex].index
                            : blocks.length)
                    ) {
                        break;
                    }
                    const block = blocks[i];
                    if (block.type === 'heading2' || block.type === 'heading3') {
                        hasLowerLevelHeading = true;
                        break;
                    }
                }
            } else if (level === 2) {
                // h2: 检查是否有 h3
                for (let i = blockIndex + 1; i < blocks.length; i++) {
                    if (
                        i >=
                        (nextHeadingIndex < headingInfos.length
                            ? headingInfos[nextHeadingIndex].index
                            : blocks.length)
                    ) {
                        break;
                    }
                    const block = blocks[i];
                    if (block.type === 'heading3') {
                        hasLowerLevelHeading = true;
                        break;
                    }
                }
            }

            // 如果没有更低级的 heading，则是最低一节
            isLowestChapter = !hasLowerLevelHeading;
        }

        isLowestChapterMap.set(headingInfo.blockId, isLowestChapter);
    });

    // 第三步：标记参考文献章节范围（需要保护的 block）
    const protectedBlockIds = new Set();
    blocks.forEach((block, index) => {
        if (block.chapterScope === '参考文献') {
            // 获取当前标题的等级
            const level = block.scopeLevel || getHeadingLevel(block.type) || 1;
            // 标记从当前位置到下一个同级/更高级标题的所有 block
            for (let i = index; i < blocks.length; i++) {
                const currentBlock = blocks[i];
                // 如果不是第一个 block 且是标题类型
                if (i > index && isHeadingType(currentBlock.type)) {
                    const currentLevel = getHeadingLevel(currentBlock.type);
                    // 遇到同级或更高级标题停止
                    if (currentLevel <= level) break;
                }
                protectedBlockIds.add(currentBlock.id);
            }
        }
    });

    // 第四步：转换 blocks，传入 isLowestChapter 和 isProtected 信息
    const content = [];
    blocks.forEach((block) => {
        const isLowestChapter = isLowestChapterMap.get(block.id) || false;
        const isProtected = protectedBlockIds.has(block.id);
        const chapterScope = block.chapterScope || null;
        const blockNode = convertSingleBlock(block, isLowestChapter, isProtected, chapterScope);
        if (blockNode) {
            content.push(blockNode);
        }
    });

    return {
        type: 'doc',
        content,
    };
};

// 辅助函数：解析包含格式标记的文本（支持 <sup>上标、**加粗**、*斜体*）
const parseTextWithHTML = (text) => {
    // 如果文本为空、null 或 undefined，返回空数组
    // 注意：TipTap 不允许空文本节点 { type: 'text', text: '' }
    if (!text && text !== 0) {
        return [];
    }

    const textStr = String(text);

    // 检查是否包含需要解析的标记
    const hasMarks = textStr.includes('<sup>') || textStr.includes('**') || textStr.includes('*');
    if (!hasMarks) {
        return [{ type: 'text', text: textStr }];
    }

    // 综合正则：匹配 <sup>...</sup>、**...**（加粗）、*...*（斜体，但不匹配 **）
    // 注意顺序：先匹配 ** 再匹配 *，避免冲突
    const combinedRegex = /<sup>(.*?)<\/sup>|\*\*(.+?)\*\*|(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g;

    const nodes = [];
    let lastIndex = 0;
    let match;

    while ((match = combinedRegex.exec(textStr)) !== null) {
        // 添加标记前的普通文本
        if (match.index > lastIndex) {
            const beforeText = textStr.substring(lastIndex, match.index);
            if (beforeText) {
                nodes.push({ type: 'text', text: beforeText });
            }
        }

        if (match[1] !== undefined) {
            // <sup>...</sup> 匹配
            nodes.push({
                type: 'text',
                text: match[1],
                marks: [{ type: 'superscript' }],
            });
        } else if (match[2] !== undefined) {
            // **...** 加粗匹配
            nodes.push({
                type: 'text',
                text: match[2],
                marks: [{ type: 'bold' }],
            });
        } else if (match[3] !== undefined) {
            // *...* 斜体匹配
            nodes.push({
                type: 'text',
                text: match[3],
                marks: [{ type: 'italic' }],
            });
        }

        lastIndex = match.index + match[0].length;
    }

    // 添加剩余的文本
    if (lastIndex < textStr.length) {
        const remainingText = textStr.substring(lastIndex);
        if (remainingText) {
            nodes.push({ type: 'text', text: remainingText });
        }
    }

    return nodes.length > 0 ? nodes : [{ type: 'text', text: textStr }];
};

// 辅助函数：安全创建文本节点数组（保持向后兼容）
const createTextContent = (text) => {
    return parseTextWithHTML(text);
};

// 辅助函数：解析包含行内公式的文本（$...$ 或 \(...\)）
const parseTextWithInlineMath = (text, blockId, detailId) => {
    if (!text || typeof text !== 'string') {
        return { hasInlineMath: false, nodes: [] };
    }

    const nodes = [];
    let hasInlineMath = false;

    // 匹配行内公式：
    // 1. $...$ （但不匹配 $$...$$）- 使用负向预查确保不匹配 $$
    // 2. \(...\) - LaTeX 标准行内公式格式
    const inlineMathRegex = /(?<!\$)\$(?!\$)([^\$]+?)\$(?!\$)|\\\((.+?)\\\)/g;
    let lastIndex = 0;
    let match;

    while ((match = inlineMathRegex.exec(text)) !== null) {
        hasInlineMath = true;

        // 添加公式前的普通文本
        if (match.index > lastIndex) {
            const beforeText = text.substring(lastIndex, match.index);
            if (beforeText) {
                // 普通文本也需要解析 HTML 标签（如 <sup>）
                const textNodes = parseTextWithHTML(beforeText);
                nodes.push(...textNodes);
            }
        }

        // 添加行内公式节点
        // match[1] 是 $...$ 格式捕获的内容，match[2] 是 \(...\) 格式捕获的内容
        const latex = (match[1] || match[2]).trim();
        nodes.push({
            type: 'inlineMath',
            attrs: { latex: latex, blockId: blockId, detailId: detailId },
        });

        lastIndex = match.index + match[0].length;
    }

    // 添加剩余的文本
    if (lastIndex < text.length) {
        const remainingText = text.substring(lastIndex);
        if (remainingText) {
            const textNodes = parseTextWithHTML(remainingText);
            nodes.push(...textNodes);
        }
    }

    return { hasInlineMath, nodes: nodes.length > 0 ? nodes : parseTextWithHTML(text) };
};

// 转换单个block为TipTap节点格式
export const convertSingleBlock = (
    block,
    isLowestChapter = false,
    isProtected = false,
    chapterScope = null,
) => {
    const { id, type, content, metadata, detailId } = block;

    switch (type) {
        case 'heading1':
            return {
                type: 'heading',
                attrs: {
                    level: 1,
                    blockId: id,
                    detailId: detailId,
                    isLowestChapter: isLowestChapter,
                    isProtected: isProtected,
                    chapterScope: chapterScope,
                },
                content: createTextContent(content),
            };

        case 'heading2':
            return {
                type: 'heading',
                attrs: {
                    level: 2,
                    blockId: id,
                    detailId: detailId,
                    isLowestChapter: isLowestChapter,
                    isProtected: isProtected,
                    chapterScope: chapterScope,
                },
                content: createTextContent(content),
            };

        case 'heading3':
            return {
                type: 'heading',
                attrs: {
                    level: 3,
                    blockId: id,
                    detailId: detailId,
                    isLowestChapter: isLowestChapter,
                    isProtected: isProtected,
                    chapterScope: chapterScope,
                },
                content: createTextContent(content),
            };

        case 'text':
            // 检测是否是块级公式（$$...$$ 或 \[...\] 包裹）
            if (content && typeof content === 'string') {
                const trimmedContent = content.trim();
                // 匹配 $$...$$ 格式的块级公式
                if (trimmedContent.startsWith('$$') && trimmedContent.endsWith('$$')) {
                    // 提取公式内容（去掉 $$ 符号和首尾空白）
                    const latex = trimmedContent.slice(2, -2).trim();
                    return {
                        type: 'blockMath',
                        attrs: {
                            latex: latex,
                            blockId: id,
                            detailId: detailId,
                            isProtected: isProtected,
                        },
                    };
                }

                // 匹配 \[...\] 格式的块级公式（LaTeX 标准格式）
                if (trimmedContent.startsWith('\\[') && trimmedContent.endsWith('\\]')) {
                    // 提取公式内容（去掉 \[ 和 \] 符号和首尾空白）
                    const latex = trimmedContent.slice(2, -2).trim();
                    return {
                        type: 'blockMath',
                        attrs: {
                            latex: latex,
                            blockId: id,
                            detailId: detailId,
                            isProtected: isProtected,
                        },
                    };
                }

                // 检测是否包含行内公式（$...$ 或 \(...\)），需要解析混合内容
                const hasInlineDollar =
                    trimmedContent.includes('$') && !trimmedContent.startsWith('$$');
                const hasInlineLatex = trimmedContent.includes('\\(');
                if (hasInlineDollar || hasInlineLatex) {
                    const parsedContent = parseTextWithInlineMath(content, id, detailId);
                    if (parsedContent.hasInlineMath) {
                        return {
                            type: 'paragraph',
                            attrs: { blockId: id, detailId: detailId, isProtected: isProtected },
                            content: parsedContent.nodes,
                        };
                    }
                }
            }

            return {
                type: 'paragraph',
                attrs: { blockId: id, detailId: detailId, isProtected: isProtected },
                content: createTextContent(content),
            };

        case 'formula':
            if (block.displayMode === 'inline') {
                // 行内公式需要包裹在段落中
                return {
                    type: 'paragraph',
                    attrs: { blockId: null, detailId: null, isProtected: isProtected },
                    content: [
                        {
                            type: 'inlineMath',
                            attrs: {
                                latex: content,
                                blockId: id,
                                detailId: detailId,
                                isProtected: isProtected,
                            },
                        },
                    ],
                };
            } else {
                // 块级公式
                return {
                    type: 'blockMath',
                    attrs: {
                        latex: content,
                        blockId: id,
                        detailId: detailId,
                        isProtected: isProtected,
                    },
                };
            }

        case 'image':
            // 处理普通图片、mermaid 和 latex 图片
            const imageType = metadata?.extraData?.imageType;
            const attrs = {
                src: content,
                alt: metadata?.caption || '图片',
                blockId: id,
                detailId: detailId,
                isProtected: isProtected,
            };

            // 如果是 mermaid 或 latex，保存原始代码以便后续可能需要编辑
            if (imageType === 'mermaid' || imageType === 'latex') {
                attrs.imageType = imageType;
                attrs.originalCode = metadata?.extraData?.originalCode || '';
            }

            return {
                type: 'image',
                attrs,
            };

        case 'code':
            // 代码块
            const language = metadata?.extraData?.language || null;
            return {
                type: 'codeBlock',
                attrs: {
                    language: language,
                    blockId: id,
                    detailId: detailId,
                    isProtected: isProtected,
                },
                content: createTextContent(content),
            };

        case 'table':
            return convertTableBlock(block, isProtected);

        case 'list':
            return convertListBlock(block, isProtected);

        default:
            console.warn(`Unknown block type: ${type}`);
            return null;
    }
};

// 解析 Markdown 表格行（去掉首尾的 | 并按 | 分割）
const parseMarkdownTableRow = (line) => {
    const trimmed = line.trim();
    // 去掉首尾的 |
    const withoutPipes = trimmed.replace(/^\||\|$/g, '').trim();
    // 按 | 分割并 trim 每个单元格
    return withoutPipes.split('|').map((cell) => cell.trim());
};

// 判断是否是分隔行（包含 :---、---、:-: 等）
const isSeparatorRow = (line) => {
    const trimmed = line.trim();
    // 如果行不包含 |，不是分隔行
    if (!trimmed.includes('|')) {
        return false;
    }
    // 去掉首尾的 |，然后按 | 分割
    const withoutPipes = trimmed.replace(/^\||\|$/g, '').trim();
    const cells = withoutPipes.split('|').map((cell) => cell.trim());
    // 检查每个单元格是否只包含分隔符（:、-、空格）
    return cells.every((cell) => /^:?-+:?$/.test(cell));
};

// 转换表格block（仅支持 Markdown 表格格式）
const convertTableBlock = (block, isProtected = false) => {
    const { id, content, detailId } = block;

    // Markdown 表格格式：| Header | Header |
    //                    | :--- | :--- |
    //                    | Cell | Cell |
    const lines =
        typeof content === 'string' ? content.split('\n').filter((line) => line.trim()) : [];

    let headers = [];
    let rows = [];

    if (lines.length === 0) {
        // 空表格：创建默认的空表格结构（TipTap 要求 table 至少有一个 tableRow）
        return {
            type: 'table',
            attrs: {
                blockId: id,
                detailId: detailId || null,
                tableContent: content || null,
                isProtected: isProtected,
            },
            content: [
                {
                    type: 'tableRow',
                    content: [
                        {
                            type: 'tableHeader',
                            content: [
                                {
                                    type: 'paragraph',
                                    content: [{ type: 'text', text: ' ' }],
                                },
                            ],
                        },
                    ],
                },
            ],
        };
    } else {
        // 找到第一个非分隔行作为表头
        let headerIndex = 0;
        while (headerIndex < lines.length && isSeparatorRow(lines[headerIndex])) {
            headerIndex++;
        }

        if (headerIndex >= lines.length) {
            // 所有行都是分隔行，无效表格
            headers = [];
            rows = [];
        } else {
            // 第一行非分隔行是表头
            headers = parseMarkdownTableRow(lines[headerIndex]);

            // 从表头行的下一行开始处理数据行，跳过分隔行
            let startIndex = headerIndex + 1;
            if (startIndex < lines.length && isSeparatorRow(lines[startIndex])) {
                // 如果紧接着的是分隔行，跳过它
                startIndex++;
            }

            // 解析数据行，过滤掉所有分隔行
            rows = lines
                .slice(startIndex)
                .filter((line) => !isSeparatorRow(line))
                .map((line) => parseMarkdownTableRow(line));
        }
    }

    const tableRows = [];

    // 表头行
    if (headers && headers.length > 0) {
        tableRows.push({
            type: 'tableRow',
            content: headers.map((header) => ({
                type: 'tableHeader',
                content: [
                    {
                        type: 'paragraph',
                        content: createTextContent(header),
                    },
                ],
            })),
        });
    }

    // 数据行
    if (rows && rows.length > 0) {
        rows.forEach((row) => {
            tableRows.push({
                type: 'tableRow',
                content: row.map((cell) => ({
                    type: 'tableCell',
                    content: [
                        {
                            type: 'paragraph',
                            content: createTextContent(cell),
                        },
                    ],
                })),
            });
        });
    }

    // 如果 tableRows 为空，创建默认结构（TipTap 要求 table 至少有一个 tableRow）
    if (tableRows.length === 0) {
        tableRows.push({
            type: 'tableRow',
            content: [
                {
                    type: 'tableHeader',
                    content: [
                        {
                            type: 'paragraph',
                            content: [{ type: 'text', text: ' ' }],
                        },
                    ],
                },
            ],
        });
    }

    return {
        type: 'table',
        attrs: {
            blockId: id,
            detailId: detailId || null,
            tableContent: content || null, // 保存原始 Markdown 表格内容
            isProtected: isProtected,
        },
        content: tableRows,
    };
};

// 转换列表block
const convertListBlock = (block, isProtected = false) => {
    const { id, content, listType, detailId } = block;
    const items = content.split('\\n').filter((item) => item.trim());

    const listItems = items.map((item) => {
        // 移除开头的数字或破折号
        const text = item
            .replace(/^[\d\-]+\.\s*/, '')
            .replace(/^[\-\*]\s*/, '')
            .trim();
        return {
            type: 'listItem',
            content: [
                {
                    type: 'paragraph',
                    content: createTextContent(text),
                },
            ],
        };
    });

    return {
        type: listType === 'ordered' ? 'orderedList' : 'bulletList',
        attrs: { blockId: id, detailId: detailId || null, isProtected: isProtected },
        content: listItems,
    };
};

// 提取选中的blockIds
export const extractBlockIdsFromSelection = (editor) => {
    const { from, to } = editor.state.selection;
    const { doc } = editor.state;
    const blockIds = new Set();

    doc.nodesBetween(from, to, (node) => {
        if (node.attrs && node.attrs.blockId) {
            blockIds.add(node.attrs.blockId);
        }
    });

    return Array.from(blockIds);
};

// 辅助函数：将表格节点转换为 Markdown 格式
const convertTableNodeToMarkdown = (tableNode) => {
    const rows = [];

    tableNode.content.forEach((rowNode) => {
        if (rowNode.type.name === 'tableRow') {
            const cells = [];
            rowNode.content.forEach((cellNode) => {
                if (cellNode.type.name === 'tableHeader' || cellNode.type.name === 'tableCell') {
                    // 提取单元格文本
                    const cellText = cellNode.textContent || '';
                    cells.push(cellText);
                }
            });
            if (cells.length > 0) {
                rows.push('| ' + cells.join(' | ') + ' |');
            }
        }
    });

    // 添加分隔行（第二行）
    if (rows.length > 0) {
        const headerRow = rows[0];
        const cellCount = headerRow.split('|').length - 2;
        const separatorRow = '| ' + Array(cellCount).fill(':---').join(' | ') + ' |';
        rows.splice(1, 0, separatorRow);
    }

    return rows.join('\n');
};

// 根据 blockId 提取块内容（Tiptap JSON 转后端格式）
export const extractBlockContentByBlockId = (editor, blockId) => {
    if (!editor || !blockId) return '';

    const { doc } = editor.state;
    let content = '';
    let found = false;

    doc.descendants((node, pos) => {
        // 已找到目标节点，跳过后续所有节点
        if (found) return false;

        if (node.attrs?.blockId === blockId) {
            // 根据节点类型提取内容
            if (node.type.name === 'image') {
                // 图片：直接使用 src URL
                content = node.attrs.src || '';
            } else if (node.type.name === 'table') {
                // 表格：始终从当前节点结构转换为 Markdown（确保获取编辑后的内容）
                content = convertTableNodeToMarkdown(node);
            } else if (node.type.name === 'paragraph' || node.type.name === 'heading') {
                // 文本：提取纯文本内容
                content = node.textContent || '';
            }
            found = true;
            return false; // 不遍历子节点
        }
    });

    return content;
};
